import { memo, useState } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { BsFilter, BsPlusLg, BsSearch } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import {
  FilterDropdown,
  FilterMenuItem,
  SearchableFilterDropdown,
} from "./EventFilterDropdowns";
import {
  PRIMARY_BTN_STYLE,
  SECONDARY_BTN_STYLE,
  type NoteTypeFilter,
} from "./attendeeNotesShared";

type AttendeeNotesToolbarProps = {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  typeFilter: NoteTypeFilter;
  onTypeFilterChange: (value: NoteTypeFilter) => void;
  eventFilter: string;
  onEventFilterChange: (value: string) => void;
  eventOptions: string[];
  eventMenuSearch: string;
  onEventMenuSearchChange: (value: string) => void;
  showMobileFilters: boolean;
  onToggleMobileFilters: () => void;
  canAddNote: boolean;
  onAddNote: () => void;
  onClearFilters: () => void;
};

function AttendeeNotesToolbar({
  searchTerm,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  eventFilter,
  onEventFilterChange,
  eventOptions,
  eventMenuSearch,
  onEventMenuSearchChange,
  showMobileFilters,
  onToggleMobileFilters,
  canAddNote,
  onAddNote,
  onClearFilters,
}: AttendeeNotesToolbarProps) {
  const { t } = useTranslation();
  const [openFilter, setOpenFilter] = useState<"type" | "event" | null>(null);
  const hasActiveFilters = Boolean(
    searchTerm.trim() || typeFilter || eventFilter,
  );

  const typeFilterLabel =
    typeFilter === "personal"
      ? t("attendeeNotes.filterPersonal")
      : typeFilter === "event"
        ? t("attendeeNotes.filterWithEvent")
        : t("attendeeNotes.filterType");

  const searchInput = (
    <InputGroup className="rounded-3 border bg-white overflow-hidden shadow-sm w-100">
      <InputGroup.Text className="bg-white border-0 text-dark pe-0">
        <BsSearch
          size={14}
          style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,.35))" }}
          aria-hidden
        />
      </InputGroup.Text>
      <Form.Control
        type="text"
        className="border-0 shadow-none"
        placeholder={t("attendeeNotes.searchPlaceholder")}
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        aria-label={t("attendeeNotes.searchPlaceholder")}
      />
    </InputGroup>
  );

  const addNoteButton = (
    <Button
      className="rounded-2 border-0 shadow-sm fw-medium text-white d-inline-flex align-items-center justify-content-center gap-2 w-100 px-3"
      style={{ ...PRIMARY_BTN_STYLE, height: 38, minHeight: 38 }}
      onClick={onAddNote}
      disabled={!canAddNote}
    >
      <BsPlusLg aria-hidden size={14} />
      {t("attendeeNotes.addOwn")}
    </Button>
  );

  return (
    <>
      <div className="d-flex d-md-none align-items-center gap-2 mb-3">
        <div className="flex-grow-1 min-w-0">{searchInput}</div>
        <div className="d-flex gap-2 ms-auto flex-shrink-0">
          <Button
            type="button"
            variant="outline-primary"
            className="rounded-2 shadow-none d-flex align-items-center justify-content-center btn-hover-project-gradient"
            style={{
              borderColor: "#2366CD",
              color: "#2366CD",
            }}
            aria-label={t("attendeeNotes.filters")}
            aria-expanded={showMobileFilters}
            onClick={onToggleMobileFilters}
          >
            <BsFilter size={18} aria-hidden />
          </Button>
          <Button
            className="rounded-2 border-0 shadow-sm fw-medium text-white d-inline-flex align-items-center justify-content-center gap-2 px-3"
            style={{ ...PRIMARY_BTN_STYLE, height: 38, minHeight: 38 }}
            onClick={onAddNote}
            disabled={!canAddNote}
          >
            <BsPlusLg aria-hidden size={14} />
            <span className="d-none d-sm-inline">{t("attendeeNotes.addOwn")}</span>
          </Button>
        </div>
      </div>

      <div className="row g-3 mb-4 align-items-center">
        <div className="d-none d-md-block col-12 col-lg-4">{searchInput}</div>

        <div
          className={`col-6 col-md-3 col-lg ${
            showMobileFilters ? "d-block" : "d-none"
          } d-md-block`}
        >
          <FilterDropdown
            id="notes-type-filter-toggle"
            active={Boolean(typeFilter)}
            label={typeFilterLabel}
            ariaLabel={t("attendeeNotes.filterType")}
            icon="bi-funnel"
            style={{ width: "100%", minWidth: 0 }}
            autoClose
            show={openFilter === "type"}
            onToggle={(next) => setOpenFilter(next ? "type" : null)}
          >
            {(
              [
                ["", "attendeeNotes.filterAll"],
                ["personal", "attendeeNotes.filterPersonal"],
                ["event", "attendeeNotes.filterWithEvent"],
              ] as const
            ).map(([value, key]) => (
              <FilterMenuItem
                key={key}
                selected={typeFilter === value}
                className="rounded-3"
                onClick={() => {
                  onTypeFilterChange(value);
                  if (value === "personal") onEventFilterChange("");
                  setOpenFilter(null);
                }}
              >
                {t(key)}
              </FilterMenuItem>
            ))}
          </FilterDropdown>
        </div>

        <div
          className={`col-6 col-md-3 col-lg ${
            showMobileFilters ? "d-block" : "d-none"
          } d-md-block`}
        >
          <SearchableFilterDropdown
            id="notes-event-filter-toggle"
            value={eventFilter}
            options={eventOptions}
            menuSearch={eventMenuSearch}
            onMenuSearch={onEventMenuSearchChange}
            onSelect={(v) => {
              onEventFilterChange(v);
              onTypeFilterChange("event");
              onEventMenuSearchChange("");
              setOpenFilter(null);
            }}
            onClear={() => {
              onEventFilterChange("");
              onEventMenuSearchChange("");
              setOpenFilter(null);
            }}
            icon="bi-calendar-event"
            label={t("attendeeNotes.filterEvent")}
            placeholder={t("attendeeNotes.filterEventPlaceholder")}
            clearLabel={t("attendeeNotes.filterClearEvent")}
            emptyLabel={t("attendeeNotes.filterEventEmpty")}
            style={{ width: "100%", minWidth: 0 }}
            autoClose="outside"
            show={openFilter === "event"}
            onToggle={(next) => setOpenFilter(next ? "event" : null)}
          />
        </div>

        {hasActiveFilters ? (
          <div
            className={`col-12 col-md-auto ${
              showMobileFilters ? "d-block" : "d-none"
            } d-md-block`}
          >
            <Button
              type="button"
              className="rounded-2 border-0 shadow-sm fw-medium text-dark d-inline-flex align-items-center justify-content-center gap-2 w-100 px-3"
              style={{ ...SECONDARY_BTN_STYLE, height: 38, minHeight: 38 }}
              onClick={onClearFilters}
            >
              {t("attendeeNotes.clearFilters")}
            </Button>
          </div>
        ) : null}

        <div className="d-none d-md-block col-12 col-md-3 col-lg-auto">
          {addNoteButton}
        </div>
      </div>
    </>
  );
}

export default memo(AttendeeNotesToolbar);
